
import { Component, Vue } from "vue-property-decorator";
import { PayPackage } from "@/common/entity/PayPackage";
import { User } from "@/common/entity/User";
import { UiTools } from "@/common/UiTools";
import QRCode from "qrcode";
import { Message } from "element-ui";
import { IWxOrder, WxOrder } from "@/common/entity/WxOrder";
import { appEnv } from "@/common/AppConst";
import { RouterTools } from "@/common/RouterTools";
import { RootMutationNames, RootState } from "@/vuex-store";
import { OtherApi } from "@/request/other";
import { Response } from "@/common/entity/Response";
import { WechatApi } from "@/request/wechat";
import { AppConfigs } from "@/common/entity/AppConfigs";
import { AliPayApi } from "@/request/alipay";
import { logError, logToConsole } from "@/common/LogHelper";
import { PayOrder } from "@/common/entity/PayOrder";
import { PayOrderApi } from "@/request/pay-order";
import { UserApi } from "@/request/user";

const DURATION_QUERY_ORDER_MS = 1000;
const PAY_RETURN_PATH = "/pay/return";

@Component
export default class Pay extends Vue {
    readonly qrCodeSize = 180;
    readonly refNames = {
        qrCode: "qrCode",
    };

    payPkgs: PayPackage[] = [];
    highlightedPkg: PayPackage | null = null;
    payingPkg: PayPackage | null = null;
    isPaying: boolean = false;
    qrCodeTip: string = "";
    aliPayTip: string = "";
    aliPayReady: boolean = false;
    aliPayLoading: boolean = false;
    queryingAliOrder: boolean = false;
    wxOrderId: number | null = null;
    checkoutSessionId: number = 0;

    get featuredIndex(): number {
        if (this.payPkgs.length <= 1) return 0;
        return Math.floor(this.payPkgs.length / 2);
    }

    get payingPkgName(): string {
        return this.payingPkg?.name || "";
    }

    get payingCount(): string {
        if (!this.payingPkg?.count) return "";
        return this.payingPkg.count + "次";
    }

    get payingFee(): number {
        return (this.payingPkg?.fee || 0) / 100;
    }

    get userInfo(): User | null {
        return (this.$store.state as RootState).currUser;
    }

    get isLogin(): boolean {
        return !!this.userInfo;
    }

    get qrCode() {
        return this.$refs[this.refNames.qrCode];
    }

    get apiConfigs(): AppConfigs | null {
        return (this.$store.state as RootState).appConfigs;
    }

    created() {
        this.reqPayPkgs();

        logToConsole("支付界面query参数", this.$route.query);
        const outTradeNo = this.$route.query.out_trade_no;
        if (!outTradeNo || typeof outTradeNo !== "string") return;
        this.queryAliOrder(outTradeNo);
    }

    deactivated() {
        this.highlightedPkg = null;
        this.resetCheckout();
    }

    unitPriceText(pkg: PayPackage): string {
        if (!pkg.count || pkg.count <= 0) return "";
        const perYuan = pkg.fee / 100 / pkg.count;
        const formatted =
            perYuan % 1 === 0 ? perYuan.toFixed(0) : perYuan.toFixed(2);
        return `¥${formatted}/次`;
    }

    isPkgHighlighted(pkg: PayPackage): boolean {
        return this.highlightedPkg?.id === pkg.id;
    }

    isPkgPaying(pkg: PayPackage): boolean {
        return this.payingPkg?.id === pkg.id;
    }

    /** 点击套餐卡片：仅高亮，不展示支付 */
    focusPkg(pkg: PayPackage) {
        this.highlightedPkg = pkg;
        if (this.payingPkg && this.payingPkg.id !== pkg.id) {
            this.checkoutSessionId++;
            this.payingPkg = null;
            this.resetPayState();
        }
    }

    /** 点击「选择套餐」按钮：展示支付并同步调用支付接口 */
    confirmPkg(pkg: PayPackage) {
        if (!this.isLogin) {
            UiTools.toLogin();
            return;
        }
        if (this.isPaying) return;

        this.highlightedPkg = pkg;

        if (this.payingPkg?.id === pkg.id) {
            this.$nextTick(() => {
                const el = this.$el.querySelector(".pay-page__checkout");
                el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            });
            return;
        }

        this.checkoutSessionId++;
        const sessionId = this.checkoutSessionId;

        this.payingPkg = pkg;
        this.resetPayState();

        this.$nextTick(() => {
            const el = this.$el.querySelector(".pay-page__checkout");
            el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            this.preparePayments(sessionId);
        });
    }

    cancelCheckout() {
        if (this.isPaying) return;
        this.checkoutSessionId++;
        this.resetCheckout();
    }

    resetPayState() {
        this.qrCodeTip = "";
        this.aliPayTip = "";
        this.aliPayReady = false;
        this.aliPayLoading = false;
        this.wxOrderId = null;
    }

    resetCheckout() {
        this.payingPkg = null;
        this.resetPayState();
        this.isPaying = false;
    }

    /** 选中套餐后同步准备支付宝与微信支付 */
    async preparePayments(sessionId: number) {
        if (!this.payingPkg || sessionId !== this.checkoutSessionId) return;

        const tasks: Promise<void>[] = [];

        if (this.apiConfigs?.showWxPay) {
            tasks.push(this.generateWxQrCode(sessionId));
        }

        if (this.apiConfigs?.showAliPay) {
            tasks.push(this.prepareAliPay(sessionId));
        }

        await Promise.all(tasks);
    }

    async prepareAliPay(sessionId: number) {
        if (!this.payingPkg || sessionId !== this.checkoutSessionId) return;
        this.aliPayTip = "";
        this.aliPayReady = true;
    }

    clickAliPayBtn() {
        if (!this.payingPkg || !this.aliPayReady || this.aliPayLoading) return;
        this.aliPayLoading = true;
        AliPayApi.sendPayForm(
            this.payingPkg.id,
            appEnv.localUrl + PAY_RETURN_PATH
        );
    }

    async generateWxQrCode(sessionId: number) {
        if (!this.payingPkg || sessionId !== this.checkoutSessionId) return;
        this.qrCodeTip = "生成二维码中...";
        this.wxOrderId = null;
        try {
            const wxOrder: IWxOrder = await WechatApi.payQrCode(
                this.payingPkg.id
            );
            if (sessionId !== this.checkoutSessionId) return;

            this.wxOrderId = wxOrder.id;
            await this.$nextTick();
            if (sessionId !== this.checkoutSessionId) return;

            QRCode.toCanvas(
                this.qrCode,
                wxOrder.qrCodeUrl,
                { errorCorrectionLevel: "H", width: this.qrCodeSize },
                (error: any) => {
                    if (sessionId !== this.checkoutSessionId) return;
                    if (error) this.qrCodeTip = this.handleQrCodeErr(error);
                    else {
                        this.qrCodeTip = "";
                        this.queryOrder(wxOrder.id, sessionId);
                    }
                }
            );
        } catch (error) {
            if (sessionId !== this.checkoutSessionId) return;
            this.qrCodeTip = this.handleQrCodeErr(error);
        }
    }

    async queryOrder(orderId: number, sessionId: number) {
        if (sessionId !== this.checkoutSessionId) return;
        const userOrder = await WechatApi.queryOrder(orderId, false);
        const order = new WxOrder(userOrder.order);
        switch (order.tradeState) {
            case "SUCCESS":
                if (userOrder.user)
                    this.$store.commit(
                        RootMutationNames.setCurrUser,
                        userOrder.user
                    );
                this.resetCheckout();
                Message.success({
                    message: order.tradeStateText,
                    showClose: true,
                });
                this.$router.push(RouterTools.pageNames.writing);
                return;
            case "PAYERROR":
                this.resetCheckout();
                Message.error({
                    message: order.tradeStateText,
                    showClose: true,
                    duration: 0,
                });
                return;
            case "REFUND":
            case "CLOSED":
                this.resetCheckout();
                Message.warning({
                    message: order.tradeStateText,
                    showClose: true,
                    duration: 0,
                });
                return;
            case "USERPAYING":
                this.isPaying = true;
                setTimeout(() => {
                    this.queryOrder(orderId, sessionId);
                }, DURATION_QUERY_ORDER_MS);
                return;
            case "NOTPAY":
                if (sessionId !== this.checkoutSessionId) return;
                setTimeout(() => {
                    this.queryOrder(orderId, sessionId);
                }, DURATION_QUERY_ORDER_MS);
                return;
            case "REVOKED":
                this.resetCheckout();
                return;
            default:
                this.resetCheckout();
                Message.error({
                    message:
                        "订单查询异常，可稍后刷新界面检查是否购买成功，如有问题可联系客服",
                    showClose: true,
                    duration: 0,
                });
        }
    }

    handleQrCodeErr(error: any): string {
        return error instanceof Response
            ? error.userMsg
            : appEnv.isProd
            ? Response.MSG_APP_EXCEPTION
            : JSON.stringify(error);
    }

    async reqPayPkgs() {
        this.payPkgs = (await OtherApi.payPkgs()).map((pkg) => {
            return new PayPackage(pkg);
        });
    }

    async queryAliOrder(outTradeNo: string) {
        this.queryingAliOrder = true;
        try {
            const response = await PayOrderApi.aliOrder(outTradeNo);
            const order: PayOrder = new PayOrder(response.order);

            if (order.isPaying) {
                setTimeout(() => {
                    this.queryAliOrder(outTradeNo);
                }, DURATION_QUERY_ORDER_MS);
                return;
            }

            if (order.isPaid) {
                const user = await UserApi.reqUserInfo(false);
                this.$store.commit(RootMutationNames.setCurrUser, user);

                this.queryingAliOrder = false;
                Message.success("支付成功");
                RouterTools.pushTo("writing");
                return;
            }

            if (order.isPayFail) {
                this.queryingAliOrder = false;
                Message.error("支付失败，如有问题可联系客服");
                return;
            }

            logError("订单状态异常，order=", order);
            this.queryingAliOrder = false;
            if (order.exection_msg) {
                Message.error({
                    message:
                        "订单异常，可稍后刷新界面检查是否购买成功，如有问题可联系客服",
                    duration: 0,
                });
            }
        } catch (error) {
            this.queryingAliOrder = false;
        }
    }
}
